import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAssignedWorksheetsAPI,
  deleteAssignedWorksheetAPI,
  deleteAssignmentAPI,
  updateAssignmentAPI,
  updateAssignedWorksheetsAPI,
} from "./worksheetAPI";

const initialState = {
  results: [],
  current_page: 1,
  total_pages: 1,
  error: null,
};

export const fetchAssignedWorksheets = createAsyncThunk(
  "worksheets/fetchAssignedWorksheets",
  async ({ page, limit = 13, type, keyword = null }) => {
    const response = await fetchAssignedWorksheetsAPI(
      page,
      limit,
      type,
      keyword
    );
    return response;
  }
);

export const updateAssignedWorksheets = createAsyncThunk(
  "worksheets/updateAssignedWorksheets",
  async ({ worksheetId, data }, { rejectWithValue }) => {
    try {
      const response = await updateAssignedWorksheetsAPI(worksheetId, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAssignment = createAsyncThunk(
  "worksheet/updateAssignment",
  async ({ data }, { rejectWithValue }) => {
    const response = await updateAssignmentAPI(data);
    return response;
  }
);

export const deleteAssignment = createAsyncThunk(
  "worksheets/deleteAssignment",
  async ({ assignmentId }, { rejectWithValue }) => {
    const response = await deleteAssignmentAPI(assignmentId);
    return response;
  }
);

export const deleteAssignedWorksheet = createAsyncThunk(
  "worksheets/deleteAssignedWorksheet",
  async ({ worksheetId }, { rejectWithValue }) => {
    const response = await deleteAssignedWorksheetAPI(worksheetId);
    return response;
  }
);

export const deleteAssignedWorksheetList = createAsyncThunk(
  "students/deleteAssignedWorksheetList",
  async ({ worksheetIds }, { rejectWithValue }) => {
    const successfulIds = [];
    const failedIds = [];

    await Promise.all(
      worksheetIds.map(async (worksheetId) => {
        try {
          await deleteAssignedWorksheetAPI(worksheetId);
          successfulIds.push(worksheetId); // 성공한 ID 추가
        } catch (error) {
          failedIds.push({
            worksheetId,
            error: error.response?.data || error.message, // 실패한 ID와 에러 메시지 저장
          });
        }
      })
    );

    return { successfulIds, failedIds };
  }
);

const assignedWorksheetListSlice = createSlice({
  name: "assigned_worksheets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignedWorksheets.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAssignedWorksheets.fulfilled, (state, action) => {
        const { results, current_page, total_pages } = action.payload;
        state.results = results;
        state.current_page = current_page;
        state.total_pages = total_pages;
      })
      .addCase(fetchAssignedWorksheets.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(updateAssignedWorksheets.pending, (state) => {
        state.error = null;
      })
      .addCase(updateAssignedWorksheets.fulfilled, (state, action) => {
        const worksheetId = action.payload.id;
        state.results = state.results.map((worksheet) => {
          if (worksheet.id === worksheetId) {
            return action.payload;
          }
          return worksheet;
        });
      })
      .addCase(updateAssignedWorksheets.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteAssignedWorksheetList.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssignedWorksheetList.fulfilled, (state, action) => {
        state.results = state.results.filter(
          (worksheet) => !action.payload.successfulIds.includes(worksheet.id) // 성공한 ID 제외
        );
      })
      .addCase(deleteAssignedWorksheetList.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteAssignedWorksheet.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssignedWorksheet.fulfilled, (state, action) => {
        state.results = state.results.filter(
          (worksheet) => worksheet.id !== action.payload.id
        );
      })
      .addCase(updateAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.results = state.results.map((worksheet) => {
          if (worksheet.id === action.payload.id) {
            return action.payload;
          }
          return worksheet;
        });
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteAssignment.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        const { id, worksheet_id } = action.payload;
        state.results = state.results.map((worksheet) => {
          if (worksheet.id === worksheet_id) {
            return {
              ...worksheet,
              student_count: worksheet.student_count - 1,
              assignment_list: worksheet.assignment_list.filter(
                (assignment) => assignment.id !== id
              ),
            };
          }
          return worksheet;
        });
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export default assignedWorksheetListSlice.reducer;
